import React, { useState, useRef, useDeferredValue } from 'react';
import { Search, ChevronLeft, ChevronRight, Image as ImageIcon } from 'lucide-react';

export const CantinaProductSection = React.memo(({
  availableProducts,
  activeBooking,
  handleDirectAddToCart,
  handleRemoveFromTicket,
  children
}: any) => {
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const deferredQuery = useDeferredValue(productSearchQuery);
  const carouselRef = useRef<HTMLDivElement>(null);
  const hasDragged = useRef(false);
  const [isCarouselDragging, setIsCarouselDragging] = useState(false);
  const [carouselStartX, setCarouselStartX] = useState(0);
  const [carouselScrollLeft, setCarouselScrollLeft] = useState(0);

  return (
    <>
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 mb-4 w-full">
        {children}
        <div className="relative flex-1">
        <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
          <Search size={12} className="text-zinc-500" />
        </div>
        <input
          type="text"
          value={productSearchQuery}
          onChange={(e) => setProductSearchQuery(e.target.value)}
          placeholder="Buscar producto..."
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          className="w-full bg-[#1e202b] border border-[#2d313f] rounded-xl pl-8 pr-3 py-1.5 text-[11px] text-white focus:outline-none focus:border-theme-primary/60 transition-all placeholder-zinc-500"
        />
      </div>
      
      </div>
      <div className="col-span-full bg-[#15171d] border border-[#2d313f] p-2 rounded-xl mt-4">
        {availableProducts.length > 0 ? (
          <div className="relative group/carousel">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                if (carouselRef.current) carouselRef.current.scrollBy({ left: -200, behavior: 'smooth' });
              }}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-black/60 hover:bg-theme-primary text-white p-1 rounded-r-lg opacity-0 group-hover/carousel:opacity-100 transition-opacity disabled:opacity-0"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                if (carouselRef.current) carouselRef.current.scrollBy({ left: 200, behavior: 'smooth' });
              }}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-black/60 hover:bg-theme-primary text-white p-1 rounded-l-lg opacity-0 group-hover/carousel:opacity-100 transition-opacity disabled:opacity-0"
            >
              <ChevronRight size={16} />
            </button>

            <div 
              ref={carouselRef}
              onMouseDown={(e) => {
                hasDragged.current = false;
                setIsCarouselDragging(true);
                if (carouselRef.current) {
                  setCarouselStartX(e.pageX - carouselRef.current.offsetLeft);
                  setCarouselScrollLeft(carouselRef.current.scrollLeft);
                }
              }}
              onMouseLeave={() => setIsCarouselDragging(false)}
              onMouseUp={() => setIsCarouselDragging(false)}
              onMouseMove={(e) => {
                if (!isCarouselDragging) return;
                e.preventDefault();
                if (carouselRef.current) {
                  const x = e.pageX - carouselRef.current.offsetLeft;
                  const walk = (x - carouselStartX) * 1.5;
                  if (Math.abs(walk) > 5) hasDragged.current = true;
                  carouselRef.current.scrollLeft = carouselScrollLeft - walk;
                }
              }}
              onWheel={(e) => {
                if (carouselRef.current && e.deltaY !== 0) {
                  carouselRef.current.scrollLeft += e.deltaY;
                }
              }}
              className="flex gap-2 overflow-x-auto pt-2 px-1 pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden cursor-grab active:cursor-grabbing"
            >
              {availableProducts
                .filter((p: any) => p.name.toLowerCase().includes(deferredQuery.toLowerCase()))
                .map((prod: any) => {
              const qtyMatch = (activeBooking.items || []).find((i: any) => i.id.startsWith(prod.id + '_'));
              const qty = qtyMatch ? qtyMatch.quantity : 0;
              const isPaid = activeBooking.status === 'paid';

              return (
                <div
                  key={prod.id}
                  onClick={() => {
                    if (hasDragged.current) return;
                    if (!isPaid) {
                      handleDirectAddToCart(prod);
                    }
                  }}
                  className={`relative flex flex-col items-center justify-between p-1 rounded-lg border transition-all duration-200 w-[70px] sm:w-[85px] h-[70px] sm:h-[85px] shrink-0 bg-[#1e202b] border-[#2d313f] group select-none ${!isPaid ? 'cursor-pointer hover:border-theme-primary/50 hover:bg-[#252836] active:scale-95' : 'opacity-50 cursor-not-allowed grayscale'}`}
                  title={isPaid ? "La reserva ya está pagada" : "Agregar a la cuenta"}
                >
                  {qty > 0 && !isPaid && (
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        if (hasDragged.current) return;
                        handleRemoveFromTicket(prod);
                      }}
                      className="absolute -top-1.5 -right-1.5 bg-blue-500 text-white font-black text-[10px] w-5 h-5 flex items-center justify-center rounded-full shadow-lg border border-blue-400 z-10 hover:bg-red-500 hover:border-red-600 transition-colors group/badge cursor-pointer select-none"
                    >
                      <span className="group-hover/badge:hidden">{qty}</span>
                      <span className="hidden group-hover/badge:block text-xs leading-none mt-[1px]">-</span>
                    </div>
                  )}
                  <div className="w-full flex justify-center flex-1 items-center min-h-[42px] mt-1">
                    {prod.imageUrl ? (
                      <img
                        src={prod.imageUrl}
                        alt={prod.name}
                        draggable={false}
                        className="w-[42px] h-[42px] sm:w-[48px] sm:h-[48px] rounded-md object-cover border border-[#2d313f] shrink-0 transition-transform duration-300 group-hover:scale-110"
                      />
                    ) : (
                      <div className="w-[42px] h-[42px] sm:w-[48px] sm:h-[48px] rounded-md bg-[#15171d] flex items-center justify-center border border-[#2d313f] shrink-0 transition-transform duration-300 group-hover:scale-110">
                        <ImageIcon size={18} className="text-zinc-500 sm:scale-110" />
                      </div>
                    )}
                  </div>
                  <div className="mt-0.5 w-full text-center shrink-0 pb-0.5">
                    <div className="text-[10px] sm:text-[11px] leading-tight font-bold text-white line-clamp-1 break-words text-center w-full px-0.5">
                      {prod.name}
                    </div>
                    <div className="text-[9px] sm:text-[10px] text-zinc-400 font-bold">${prod.price.toLocaleString('es-AR')}</div>
                  </div>
                </div>
              );
            })}
            </div>
          </div>
        ) : (
          <div className="text-center py-4 bg-[#1e202b] rounded-lg border border-dashed border-[#2d313f] text-zinc-500 text-xs">
            No tienes productos cargados en tu inventario.
          </div>
        )}
      </div>
    </>
  );
});
